import {
    addDoc,
    arrayRemove,
    arrayUnion,
    collection,
    doc,
    DocumentData,
    getDoc,
    getDocs,
    orderBy,
    query,
    setDoc,
    UpdateData,
    updateDoc,
    where,
    writeBatch,
} from 'firebase/firestore';

import { IChild, IUser } from '@customTypes/user';
import { ClaimRewardFormOptions } from 'components/Rewards/ClaimRewardDialog';
import { Reward } from 'components/Rewards/types';
import { formatErrorMessage } from 'external/firebase/controllers/utils';
import { auth, db } from 'external/firebase/index';

import { convertDateToString } from 'utils/formatTime';

export const getUser = async (id: string): Promise<IUser> => {
    const docRef = doc(db, 'users', id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
        throw new Error('db/no-document');
    }
    return { id: docSnap.id, ...docSnap.data() } as IUser;
};

export const getUsers = async () => {
    const q = query(collection(db, 'users'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() } as IUser));
};

export const getUsersByIds = async (ids: string[]): Promise<IUser[]> => {
    const userPromises = ids.map(async id => {
        const docRef = doc(db, 'users', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() } as IUser;
        }
        return null;
    });
    const users = (await Promise.all(userPromises)).filter(Boolean) as IUser[];
    return users;
};

export const getUsersByRole = async (role: string) => {
    const users: DocumentData[] = [];
    const q = query(collection(db, 'users'), where('role', '==', role));
    const querySnap = await getDocs(q);
    querySnap.forEach(docSnap => {
        users.push({ id: docSnap.id, ...docSnap.data() });
    });
    return users as IUser[];
};

export const getUserProfile = async (userId: string) => {
    const docRef = doc(db, 'users', userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as IUser;
    }
    return { ok: false as const, error: { code: 'db/no-document' } };
};

export const getUserByEmail = async (email: string): Promise<IUser | { ok: false; error: { code: string } }> => {
    try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('email', '==', email));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const userData = querySnapshot.docs[0].data() as IUser;
            userData.id = querySnapshot.docs[0].id;
            return userData;
        }
        return { ok: false, error: { code: 'auth/user-not-found' } };
    } catch (error) {
        return { ok: false, error: { code: 'auth/internal-error' } };
    }
};

export const addGroupToUsersBatch = async (users: { userId: string }[], groupId: string) => {
    const batch = writeBatch(db);
    users.forEach(user => {
        const docRef = doc(db, 'users', user.userId);
        batch.update(docRef, { groups: arrayUnion(groupId) } as UpdateData<never>);
    });
    await batch.commit();
    return { ok: true as const };
};

export const removeGroupFromUsersBatch = async (users: { userId: string }[], groupId: string) => {
    try {
        const batch = writeBatch(db);
        users.forEach(user => {
            const docRef = doc(db, 'users', user.userId);
            batch.update(docRef, { groups: arrayRemove(groupId) } as UpdateData<never>);
        });
        await batch.commit();
        return { ok: true as const };
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const updateParentChild = async (
    childEmail: string,
    firstName: string,
    lastName: string,
    status: string,
    parentId: string,
) => {
    const docRef = doc(db, 'users', parentId, 'children', childEmail.toLowerCase());
    await setDoc(docRef, { email: childEmail, firstName, lastName, status }, { merge: true });
};

export const getChild = async (id: string) => {
    if (!auth.currentUser) {
        throw new Error('auth/user-not-found');
    }
    const docRef = doc(db, 'users', auth.currentUser.uid, 'children', id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
        throw new Error('db/no-document');
    }
    return { id, ...docSnap.data() } as IChild;
};

export const getChildren = async (parentId: string) => {
    const children: DocumentData[] = [];
    const q = query(collection(db, 'users', parentId, 'children'));
    const querySnap = await getDocs(q);
    querySnap.forEach(docSnap => {
        children.push({ id: docSnap.id, ...docSnap.data() });
    });
    return children as IChild[];
};

export const getRegisteredChildren = async () => {
    if (!auth.currentUser) {
        throw new Error('auth/user-not-found');
    }
    const children: DocumentData[] = [];
    const q = query(collection(db, 'users'), where('parent', '==', auth.currentUser.uid));
    const querySnap = await getDocs(q);
    querySnap.forEach(docSnap => {
        children.push({ id: docSnap.id, ...docSnap.data() });
    });
    return children as IUser[];
};

export const claimReward = async (groupId: string, reward: Reward, optionSelected: ClaimRewardFormOptions) => {
    if (!auth.currentUser) {
        throw new Error('auth/user-not-found');
    }

    const currentUserProfile = await getUserProfile(auth.currentUser.uid);
    if ('error' in currentUserProfile) {
        throw new Error('db/user-profile-not-found');
    }

    if (currentUserProfile.role !== 'child') {
        throw new Error('auth/permission-denied');
    }

    const claimedRewardsRef = collection(db, 'users', auth.currentUser.uid, 'claimedRewards');

    await addDoc(claimedRewardsRef, {
        groupId,
        rewardName: reward.name,
        rewardLevel: reward.level,
        pointsRequired: reward.pointsRequired,
        optionSelected,
        date: convertDateToString(new Date()),
        claimedBy: auth.currentUser.uid,
    });

    const rewardRef = doc(db, 'rewards', `${reward.level}`);
    const rewardSnap = await getDoc(rewardRef);
    if (!rewardSnap.exists()) {
        throw new Error('reward/not-found');
    }

    const rewardData = rewardSnap.data() as Reward;
    const updatedOptions = rewardData.options.map(option =>
        option.name === optionSelected.reward && option.quantity && option.quantity > 0
            ? { ...option, quantity: option.quantity - 1 }
            : option,
    );

    await updateDoc(rewardRef, { options: updatedOptions });

    await addDoc(collection(db, 'mail'), {
        to: 'info@codeacademykids.com',
        message: {
            subject: 'CAK prizo užsakymas',
            text: `
                Užsakymo tipas: Tiesioginis užsakymas (mokinys)
                Užsakė: ${auth.currentUser.email}
                Grupė: ${groupId}
                Prizas: ${reward.name || 'Nenurodytas'}
                Pasirinkimas: ${optionSelected.reward || 'Nenurodytas'}
                Komentaras: ${optionSelected.additionalComment || 'Nenurodytas'}
                   `,
            html: `
                <strong>Užsakymo tipas:</strong> Tiesioginis užsakymas (mokinys)</br>
                <strong>Užsakė:</strong> ${auth.currentUser.email}</br>
                <strong>Grupė:</strong> ${groupId}</br>
                <strong>Prizas:</strong> ${reward.name || 'Nenurodytas'}</br>
                <strong>Pasirinkimas:</strong> ${optionSelected.reward || 'Nenurodytas'}</br>
                <strong>Komentaras:</strong> ${optionSelected.additionalComment || 'Nenurodytas'}</br>
                `,
        },
    });
};

export const getClaimedRewards = async (userId: string, groupId: string): Promise<Set<string>> => {
    if (!auth.currentUser) {
        throw new Error('auth/user-not-found');
    }

    const rewardsSet = new Set<string>();

    if (userId !== auth.currentUser.uid) {
        return rewardsSet;
    }

    const currentUserRewardsRef = collection(db, 'users', auth.currentUser.uid, 'claimedRewards');
    const currentUserQuery = query(currentUserRewardsRef, where('groupId', '==', groupId));
    const currentUserSnapshot = await getDocs(currentUserQuery);

    currentUserSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.rewardName) {
            rewardsSet.add(data.rewardName);
        }
    });

    try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
            const userData = userDoc.data();
            const parentId = userData.parent;

            if (parentId) {
                const parentRewardsRef = collection(db, 'users', parentId, 'claimedRewards');
                const parentQuery = query(parentRewardsRef, where('groupId', '==', groupId));
                const parentSnapshot = await getDocs(parentQuery);

                parentSnapshot.forEach(doc => {
                    const data = doc.data();
                    // Only include rewards that don't have claimedBy field (unmigrated)
                    if (!data.claimedBy && data.rewardName) {
                        rewardsSet.add(data.rewardName);
                    }
                });
            }
        }
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching unmigrated claimed rewards:', error);
    }

    return rewardsSet;
};

export const getRewards = async (orderDirection: 'asc' | 'desc' = 'desc'): Promise<Reward[]> => {
    const rewardsCollection = collection(db, 'rewards');
    const rewardsQuery = query(rewardsCollection, orderBy('level', orderDirection));
    const rewardsSnapshot = await getDocs(rewardsQuery);
    return rewardsSnapshot.docs.map(doc => doc.data() as Reward);
};
